const styles = ':host{display:block;position:relative}slot[name=panels]{display:block;position:relative}slot[name=panels]::slotted(*){position:absolute;top:0;visibility:hidden}slot[name=panels]::slotted(.skapa-active-panel){position:relative;visibility:visible}hr{border:none;border-bottom:1px solid rgb(var(--colour-neutral-3,223,223,223));margin:0 calc(var(--skapa-tabs-overflow-margin, .25rem)*-1);margin-top:.25rem;width:calc(100% + var(--skapa-tabs-overflow-margin, .25rem)*2)}:host([sticky]) div.wrapper{background-color:rgb(var(--colour-elevation-1,255,255,255));position:sticky;top:0;z-index:1}:host([sticky]) slot[name=panels]{--skapa-scroll-margin-top:3.25rem}skapa-overflow-carousel{--skapa-carousel-overflow-start:var(--skapa-tabs-overflow-margin,0.25rem);--skapa-carousel-overflow-end:var(--skapa-tabs-overflow-margin,0.25rem)}skapa-selection-highlight{align-self:flex-end;display:block;height:.1875rem;order:-1;pointer-events:none;width:0}skapa-selection-highlight:after{background-color:rgb(var(--colour-interactive-emphasised-border-selected,0,88,163));border-radius:.09375rem;content:"";display:block;height:.1875rem;left:calc(var(--tab-selection-offset, 0rem) + var(--skapa-tabs-overflow-margin, .25rem));min-width:var(--tab-selection-width,0);position:relative;top:.25rem;transition:left var(--skapa-reduced-motion,.2s) cubic-bezier(.4,0,.4,1);transition-property:left,min-width}skapa-overflow-carousel::part(container){--skapa-carousel-item-gap:1.5rem;--carousel-container-vertical-padding:0.25rem}skapa-selection-highlight.instant:after{display:none;transition:none}:host([skapa-rtl]) skapa-selection-highlight:after{left:unset;right:calc(var(--tab-selection-offset, 0) - var(--tab-selection-width, 0) + var(--skapa-tabs-overflow-margin, .25rem));transition-property:right,min-width}:host([subtle]) skapa-selection-highlight:after{background-color:rgb(var(--colour-interactive-subtle-border-selected,17,17,17))}@media (prefers-reduced-motion){skapa-selection-highlight{--skapa-reduced-motion:0}}';
const lightStyles = 'skapa-tabs>[role=tabpanel]{padding:2.5rem 0}skapa-tabs>:focus-visible{box-shadow:0 0 0 4px var(--skapa-focus-ring-internal,rgb(var(--colour-neutral-1,255,255,255)));outline:2px solid var(--skapa-focus-ring-external,rgb(var(--colour-neutral-7,17,17,17)));outline-offset:4px;transition:outline .2s ease-out,box-shadow .2s ease-out}skapa-tabs>[role=tab],skapa-tabs>button{align-items:center;background:none;border:0;border-radius:.125rem;box-sizing:border-box;color:rgb(var(--colour-text-and-icon-3,118,118,118));cursor:pointer;display:inline-flex;flex-shrink:0;font-family:inherit;font-size:.875rem;font-weight:700;height:2.75rem;line-height:1.4285714286;margin:0;min-height:2.75rem;padding:.25rem;text-align:start}skapa-tabs>[role=tab]:hover,skapa-tabs>button:hover{color:rgb(var(--colour-text-and-icon-2,72,72,72))}skapa-tabs>[role=tab][aria-selected=true],skapa-tabs>button[aria-selected=true]{color:rgb(var(--colour-text-and-icon-1,17,17,17));cursor:default}skapa-tabs>[role=tab]:disabled,skapa-tabs>button:disabled{color:rgb(var(--colour-interactive-disabled-1,204,204,204));cursor:default}skapa-tabs>[role=tab]>skapa-icon,skapa-tabs>button>skapa-icon{color:inherit;margin:-.5rem 0;margin-inline-end:.5rem;pointer-events:none}skapa-tabs>[role=tab]:focus-visible,skapa-tabs>button:focus-visible{box-shadow:none;outline-offset:.125rem}';
import { OverflowCarousel } from "@ingka/carousel-webc/passive.js";
import { SkapaElement } from "@ingka/skapa-webc-element";
import { RTLObserver, CachedValue } from "@ingka/webc-tools";
import {
  updateSelectionStyles,
  instantUpdateSelectionStyles
} from "./features/selection-highlight.js";
import { keyboardControl } from "./features/key-handler.js";
import { isAccessible } from "./utils/accessible-tab.js";
import {
  decorateTab,
  connectTabWithPanel,
  updateSelectedState
} from "./features/tab-decorator.js";
import { cancelledSelection } from "./features/selection-event.js";
function generateTemplate({ html }) {
  const template = document.createElement("template");
  template.innerHTML = html`
    <style>
      ${styles}
    </style>
    <div class="wrapper">
      <skapa-overflow-carousel role="tablist">
        <skapa-selection-highlight
          aria-hidden="true"
        ></skapa-selection-highlight>
        <slot class="tabs"></slot>
      </skapa-overflow-carousel>
      <hr />
    </div>
    <slot name="panels"></slot>
    <slot name="light-styles"></slot>
  `;
  const lightStyleTemplate = document.createElement("template");
  lightStyleTemplate.innerHTML = html`<style slot="light-styles">
    ${lightStyles}
  </style>`;
  return { main: template, lightStyleTemplate };
}
class Tabs extends SkapaElement {
  constructor() {
    super();
    this.initialiseTabs = () => {
      this.__tabs.updateCache = true;
      this.__panels.updateCache = true;
      const autoId = this.autoId;
      this.tabs.forEach((tab, i) => {
        const panel = this.panels[i] || void 0;
        decorateTab(tab, panel);
        if (autoId && panel)
          connectTabWithPanel(tab, panel, autoId, i);
      });
      const activeTabId = this.activeTab;
      if (activeTabId)
        this.activateTab(activeTabId, true);
      else
        this.activateTab(this.tabs.find(isAccessible)?.id, true);
    };
    // Cached list of tabs. The parameter function is used for refreshing its stored value.
    this.__tabs = new CachedValue(
      () => Array.from(
        this.tabSlot.assignedElements({ flatten: true })
      )
    );
    // Cached list of panels. The parameter function is used for refreshing its value.
    this.__panels = new CachedValue(
      () => Array.from(
        this.panelSlot.assignedElements({ flatten: true })
      )
    );
    this.handleKeydown = (e) => {
      const nextTab = keyboardControl(
        this.tabs,
        e,
        this.hasAttribute("skapa-rtl")
      );
      if (nextTab) {
        if (this.activateTab(nextTab.id)) {
          instantUpdateSelectionStyles(this);
          nextTab.focus();
        }
      }
    };
    this.handleClick = (e) => {
      const btn = e.target;
      this.activateTab(btn.id);
    };
    this.activateTab = (tabId, silent = false) => {
      if (!tabId)
        return false;
      const targetIndex = this.tabs.findIndex((tab) => tab.id === tabId);
      if (targetIndex < 0)
        return false;
      const targetTab = this.tabs[targetIndex];
      if (!isAccessible(targetTab))
        return false;
      if (!silent) {
        if (cancelledSelection(this, targetIndex))
          return false;
      }
      this.tabs.forEach(
        (tab, index) => updateSelectedState(tab, this.panels[index], tab === targetTab)
      );
      updateSelectionStyles(this, targetTab);
      return true;
    };
    this.synchTablistAria = () => {
      if (this.tablistLabel)
        this.tablist.setAttribute("aria-label", this.tablistLabel);
      else
        this.tablist.removeAttribute("aria-label");
      if (this.tablistLabelledBy)
        this.tablist.setAttribute("aria-labelledby", this.tablistLabelledBy);
      else
        this.tablist.removeAttribute("aria-labelledby");
    };
    this.gatherProperties(
      [
        "activeTab",
        "autoId",
        "sticky",
        "tablistLabel",
        "tablistLabelledBy",
        "subtle"
      ],
      this
    );
    const shadow = this.attachShadow({ mode: "open" });
    const { main, lightStyleTemplate } = this.getTemplates(generateTemplate);
    shadow.append(main.content.cloneNode(true));
    this.tabSlot = shadow.querySelector("slot.tabs");
    this.panelSlot = shadow.querySelector(
      "slot[name=panels]"
    );
    this.lightStyleNode = lightStyleTemplate.content.cloneNode(
      true
    );
    this.selectionHighlight = shadow.querySelector(
      this.getPrefixedTagName("selection-highlight")
    );
    this.tablist = shadow.querySelector("[role=tablist]");
  }
  static get observedAttributes() {
    return [
      "active-tab",
      "auto-id",
      "skapa-rtl",
      "tabllist-label",
      "tablist-labelledby"
    ];
  }
  connectedCallback() {
    super.connectedCallback();
    this.shadowRoot.addEventListener("slotchange", this.initialiseTabs);
    this.tabSlot.addEventListener("click", this.handleClick);
    this.tabSlot.addEventListener("keydown", this.handleKeydown);
    this.append(this.lightStyleNode);
    RTLObserver.addTarget(this);
    this.synchTablistAria();
    this.micro(this.initialiseTabs, "initialise tabs");
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.shadowRoot.removeEventListener("slotchange", this.initialiseTabs);
    this.tabSlot.removeEventListener("click", this.handleClick);
    this.tabSlot.removeEventListener("keydown", this.handleKeydown);
    RTLObserver.removeTarget(this);
  }
  get tabs() {
    return this.__tabs.value;
  }
  get panels() {
    return this.__panels.value;
  }
  attributeChangedCallback(attributeName) {
    if (attributeName === "active-tab") {
      this.activateTab(this.activeTab, true);
    } else if (attributeName === "skapa-rtl") {
      const currentTab = this.tabs.find((tab) => tab.ariaSelected === "true");
      if (currentTab) {
        instantUpdateSelectionStyles(this, currentTab);
      }
    } else if (attributeName === "auto-id") {
      this.initialiseTabs();
    } else if (attributeName.includes("tablist")) {
      this.synchTablistAria();
    }
  }
  get activeTab() {
    return this.getAttribute("active-tab");
  }
  set activeTab(value) {
    if (value)
      this.setAttribute("active-tab", value);
    else
      this.removeAttribute("active-tab");
  }
  get sticky() {
    return this.hasAttribute("sticky");
  }
  set sticky(toggle) {
    this.toggleAttribute("sticky", !!toggle);
  }
  get autoId() {
    return this.getAttribute("auto-id");
  }
  set autoId(value) {
    if (value)
      this.setAttribute("auto-id", value);
    else
      this.removeAttribute("auto-id");
  }
  get tablistLabel() {
    return this.getAttribute("tablist-label");
  }
  set tablistLabel(label) {
    if (label)
      this.setAttribute("tablist-label", label);
    else
      this.removeAttribute("tablist-label");
  }
  get tablistLabelledBy() {
    return this.getAttribute("tablist-labelledby");
  }
  set tablistLabelledBy(labelId) {
    if (labelId)
      this.setAttribute("tablist-labelledby", labelId);
    else
      this.removeAttribute("tablist-labelledby");
  }
  get subtle() {
    return this.hasAttribute("subtle");
  }
  set subtle(val) {
    this.toggleAttribute("subtle", !!val);
  }
}
Tabs.skapaTagName = "tabs";
Tabs.skapaDependencies = [OverflowCarousel];
export {
  Tabs
};
